// web/src/lib/functions.ts
// Typed callable wrappers for Cloud Functions via the Firebase SDK.
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";

// Callable client timeouts. The Firebase SDK default is 70s — SHORTER than
// several functions' own server `timeoutSeconds`, so a long-but-valid call
// (notably voiceDeck's over-length 2× re-synthesis, observed ~73s) gets cut off
// client-side and the whole generate fails with nothing produced. Match each
// client timeout to its server function's timeoutSeconds so the client never
// abandons a request the server is still legitimately processing.
const TIMEOUT_2MIN = 120_000; // server timeoutSeconds: 120 (refineBoxes/generateRoast/synthesizeSpeech/generateDoodle)
const TIMEOUT_5MIN = 300_000; // server timeoutSeconds: 300 (comprehendDeck)
// Dedicated voiceDeck client timeout. voiceDeck now synthesizes one TTS clip PER
// segment (~6–10 tentpoles + sweeps) and stitches them, so its server timeoutSeconds
// was raised to 300; give it its OWN client timeout (NOT the shared TIMEOUT_2MIN, which
// also wraps refineBoxes/generateRoast/synthesizeSpeech/generateDoodle).
const TIMEOUT_VOICE_DECK = 300_000; // server timeoutSeconds: 300 (voiceDeck)
import type {
  WordTiming,
  DetectedObject,
  Mode,
  Profanity,
  DoodleRequest,
  DoodleResponse,
} from "@/types/roast";
import type { AudioProfile } from "@/player/audio/types";

export const generateRoast = httpsCallable<
  {
    mode: Mode;
    profanity: Profanity;
    name?: string | null;
    context?: string | null;
    length?: "microwave" | "deepFry";
    imagePath?: string | null;
  },
  { script: string; title: string; detectedObjects: DetectedObject[]; audioProfile: AudioProfile }
>(functions, "generateRoast", { timeout: TIMEOUT_2MIN });

export const synthesizeSpeech = httpsCallable<
  { text: string; mode: Mode; voiceId?: string; profanity?: Profanity },
  { audioBase64: string; mimeType: string; wordTimings: WordTiming[] }
>(functions, "synthesizeSpeech", { timeout: TIMEOUT_2MIN });

export const generateDoodle = httpsCallable<DoodleRequest, DoodleResponse>(
  functions,
  "generateDoodle",
  { timeout: TIMEOUT_2MIN },
);

export const comprehendDeck = httpsCallable<
  { mode: Mode; profanity: Profanity; name?: string | null; context?: string | null; pdfPath: string; pageCount: number; sourceType?: "deck" | "doc" },
  { segments: { slideIndices: number[]; text: string; zoomPhrase?: string }[]; title: string; audioProfile: AudioProfile; verdict: { grade?: string; line: string } | null }
>(functions, "comprehendDeck", { timeout: TIMEOUT_5MIN });

export const refineBoxes = httpsCallable<
  { keySlides: { slideIndex: number; imageBase64: string }[]; draftRegions: Record<number, DetectedObject[]> },
  { keySlideRegions: Record<number, DetectedObject[]> }
>(functions, "refineBoxes", { timeout: TIMEOUT_2MIN });

export const detectDeckRegions = httpsCallable<
  { slides: { slideIndex: number; imageBase64: string; phrase: string; mimeType?: "image/jpeg" | "image/webp" }[] },
  { regions: Record<number, DetectedObject[]> }
>(functions, "detectDeckRegions", { timeout: TIMEOUT_2MIN });

export const generateQuips = httpsCallable<
  { data: string; mimeType: string; sourceType: "photo" | "deck" | "doc"; mode: Mode; profanity?: Profanity; count?: number },
  { quips: string[] }
>(functions, "generateQuips", { timeout: TIMEOUT_2MIN });

export const voiceDeck = httpsCallable<
  { segments: { slideIndices: number[]; text: string }[]; pageCount: number; mode: Mode; voiceId?: string; profanity?: Profanity },
  { audioBase64: string; mimeType: string; wordTimings: WordTiming[]; durationSec: number; script: string; segmentTimings: { slideIndices: number[]; clipStartSec: number; clipEndSec: number }[] }
>(functions, "voiceDeck", { timeout: TIMEOUT_VOICE_DECK });
